import { HeaderGroup } from "@tanstack/react-table";
import { RefObject, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import { TableData } from "../table";
import css from "../table.module.scss";
import { TableHeaderRows } from "./table_header_rows";

type FixedTableHeaderProps<T extends TableData> = {
  realTheadRef: RefObject<HTMLTableSectionElement | null>;
  tableRef?: RefObject<HTMLTableElement | null>;
  scrollContainerRef?: RefObject<HTMLElement | null>;
  getWrapperStyle?: () => Partial<CSSStyleDeclaration> | undefined;
  headerGroups: HeaderGroup<T>[];
  compact?: boolean;
  displayGrid?: boolean;
  enableMultiRowSelection?: boolean;
  tableClassName?: string;
};

const syncColumnWidths = (realThead: HTMLTableSectionElement, cloneThead: HTMLTableSectionElement) => {
  const realThs = realThead.querySelectorAll("th");
  const cloneThs = cloneThead.querySelectorAll("th");
  realThs.forEach((th, i) => {
    const cloneTh = cloneThs[i];
    if (cloneTh) {
      const computed = getComputedStyle(th);
      const contentWidth =
        th.getBoundingClientRect().width -
        parseFloat(computed.paddingLeft) -
        parseFloat(computed.paddingRight) -
        parseFloat(computed.borderLeftWidth) -
        parseFloat(computed.borderRightWidth);
      cloneTh.style.width = `${contentWidth}px`;
      cloneTh.style.minWidth = `${contentWidth}px`;
    }
  });
};

/**
 * Renders a fixed clone of the table header as a portal on document.body.
 * Mounts hidden (via CSS); the scroll effect reveals it when the real header
 * scrolls out of view.
 */
export const FixedTableHeader = <T extends TableData>({
  realTheadRef,
  tableRef,
  scrollContainerRef,
  getWrapperStyle,
  headerGroups,
  compact,
  displayGrid,
  enableMultiRowSelection,
  tableClassName
}: FixedTableHeaderProps<T>) => {
  const cloneTheadRef = useRef<HTMLTableSectionElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isFixedRef = useRef(false);

  // Keep getWrapperStyle in a ref so handlers always call the latest version
  // without needing to be re-registered when the function reference changes.
  const getWrapperStyleRef = useRef(getWrapperStyle);
  useEffect(() => {
    getWrapperStyleRef.current = getWrapperStyle;
  }, [getWrapperStyle]);

  // Column width sync: ResizeObserver on the real thead keeps clone widths in sync.
  useEffect(() => {
    const realThead = realTheadRef.current;
    if (!realThead) {
      return;
    }

    const observer = new ResizeObserver(() => {
      const cloneThead = cloneTheadRef.current;
      if (cloneThead) {
        syncColumnWidths(realThead, cloneThead);
      }
    });

    observer.observe(realThead);
    return () => observer.disconnect();
  }, [realTheadRef]);

  // Toggle and synchronize all clone state the moment the transition happens, before any async observer fires
  useEffect(() => {
    const realThead = realTheadRef.current;
    if (!realThead) {
      return;
    }

    const handleScroll = () => {
      const theadRect = realThead.getBoundingClientRect();
      const tableBottom = tableRef?.current?.getBoundingClientRect().bottom ?? Infinity;
      const shouldBeFixed = theadRect.top <= 0 && tableBottom > theadRect.height;
      if (shouldBeFixed === isFixedRef.current) {
        return;
      }

      isFixedRef.current = shouldBeFixed;

      const cloneThead = cloneTheadRef.current;
      const wrapper = wrapperRef.current;
      const scrollContainer = scrollContainerRef?.current;

      if (shouldBeFixed && cloneThead && wrapper && scrollContainer) {
        const styles = getWrapperStyleRef.current?.();
        styles && Object.assign(wrapper.style, styles);

        syncColumnWidths(realThead, cloneThead);

        cloneThead.style.transform = `translateX(-${scrollContainer.scrollLeft}px)`;
        wrapper.style.visibility = "visible";
      } else {
        if (cloneThead) {
          cloneThead.style.transform = "";
        }
        if (wrapper) {
          wrapper.style.visibility = "";
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [realTheadRef, tableRef]);

  // Keeps clone transform and wrapper position in sync while the header is pinned (scroll or resize)
  useEffect(() => {
    const scrollContainer = scrollContainerRef?.current;
    if (!scrollContainer) {
      return;
    }

    const handleScroll = () => {
      if (!isFixedRef.current) {
        return;
      }
      const cloneThead = cloneTheadRef.current;
      if (cloneThead) {
        cloneThead.style.transform = `translateX(-${scrollContainer.scrollLeft}px)`;
      }
    };

    const observer = new ResizeObserver(() => {
      if (!isFixedRef.current) {
        return;
      }
      const wrapper = wrapperRef.current;
      const styles = getWrapperStyleRef.current?.();
      if (wrapper && styles) {
        Object.assign(wrapper.style, styles);
      }
    });

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
    observer.observe(scrollContainer);

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
      observer.disconnect();
    };
  }, [scrollContainerRef]);

  return createPortal(
    <div ref={wrapperRef} className={css.fixedHeaderWrapper} aria-hidden>
      <table className={tableClassName}>
        <TableHeaderRows
          theadRef={cloneTheadRef}
          headerGroups={headerGroups}
          compact={compact}
          displayGrid={displayGrid}
          enableMultiRowSelection={enableMultiRowSelection}
        />
      </table>
    </div>,
    document.body
  );
};
